import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, MapPin, Navigation, Search, X } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import { formatAmount } from "@/utils/formatAmount";
import { fmtMeters } from "@/features/ze_tenders/utils";
import {
  type ClosedAreasLayer,
  createGugikLayers,
  GugikLayerPanel,
  useGugikLoading,
  useGugikState,
  useGugikToggle,
  useNidTileContent,
} from "@/components/map/gugik";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useCountyBoundaries } from "@/features/contractors/area_map/api";
import { voivodeshipBorders } from "@/features/contractors/area_map/geometry";
import { PLACE_SEARCH_MIN, usePlaceSearch, type MegamapPlace, type MegamapProject } from "./data";
import { aggregate, pixelClusters, pointInPolygon } from "./clustering";
import { colorFor, sizeMetric, type ColorDim, type SizeDim } from "./colors";

interface Props {
  rows: MegamapProject[];
  colorDim: ColorDim;
  /** Which metric a single-project marker's radius scales with. */
  sizeDim: SizeDim;
  /** Draw-a-region (lasso) mode: enables the geoman polygon tool. */
  lasso: boolean;
  /** Bump `key` to fly the map to a point (e.g. a clicked zone). */
  focus: { lat: number; lng: number; key: number } | null;
  onViewportChange: (visible: MegamapProject[]) => void;
  onLassoSelect: (items: MegamapProject[]) => void;
  /** Current map zoom — drives the zoom-dependent proximity radius. */
  onZoomChange: (zoom: number) => void;
  /** Draw voivodeship borders (from the county boundaries file). */
  voivodeshipLines: boolean;
}

// Above the GUGiK overlays (panes below 320, see createGugikLayers) and below the overlay
// pane (400) holding the project dots, so a border never covers a clickable dot.
const VOIVODESHIP_PANE = "voivodeshipBorders";

/** Compact złoty for the bubble label: "2,1 mln" / "820 tys" / "450". */
function compactPln(v: number): string {
  if (v >= 1e6) return `${(v / 1e6).toFixed(1).replace(".", ",")} mln`;
  if (v >= 1e3) return `${Math.round(v / 1e3)} tys`;
  return String(Math.round(v));
}

/** Cluster bubble label: projekty · działki pryw. nieuzgodnione · WPW niewysłane w teren ·
 * wartość. Ring turns red when the cluster holds any project still "do przydziału podwykonawcy". */
function clusterIcon(count: number, unagreed: number, pending: number, value: number, needs: number): L.DivIcon {
  const ring = needs > 0 ? "#dc2626" : "#64748b";
  const bg = needs > 0 ? "#fee2e2" : "#f1f5f9";
  const text = `${count} proj · ${unagreed} dz · ${pending} WPW · ${compactPln(value)} zł`;
  const w = Math.round(20 + text.length * 6.0);
  const h = 24;
  return L.divIcon({
    html: `<div style="display:flex;align-items:center;justify-content:center;width:${w}px;height:${h}px;border:2px solid ${ring};background:${bg};color:#0f172a;border-radius:9999px;font-weight:600;font-size:11px;white-space:nowrap;box-shadow:0 1px 4px rgba(0,0,0,.25)">${text}</div>`,
    className: "",
    iconSize: [w, h],
    iconAnchor: [w / 2, h / 2],
  });
}

/** Non-interactive label pinned ABOVE a single-project dot — same "prostokąt" the clusters get,
 * for one project: działki · WPW · wartość. `dotR` is the dot radius so the label clears it. */
function singleLabelIcon(p: MegamapProject, dotR: number): L.DivIcon {
  const ring = p.needs_subcontractor ? "#dc2626" : "#64748b";
  const bg = p.needs_subcontractor ? "#fee2e2" : "#ffffff";
  const text = `${p.private_unagreed_parcels} dz · ${p.private_owners_pending} WPW · ${compactPln(p.total_value || 0)} zł`;
  const w = Math.round(16 + text.length * 6.0);
  const h = 20;
  return L.divIcon({
    html: `<div style="display:flex;align-items:center;justify-content:center;width:${w}px;height:${h}px;border:1.5px solid ${ring};background:${bg};color:#0f172a;border-radius:9999px;font-weight:600;font-size:11px;white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,.2)">${text}</div>`,
    className: "",
    iconSize: [w, h],
    iconAnchor: [w / 2, h + dotR + 5],
  });
}

/** OSM/Nominatim classification → Polish word for the picker. Unknown types fall back to
 * the raw OSM value rather than to nothing — "co to jest" beats a blank line. */
const PLACE_TYPE_PL: Record<string, string> = {
  city: "miasto",
  town: "miasto",
  village: "wieś",
  hamlet: "przysiółek",
  isolated_dwelling: "osada",
  suburb: "dzielnica",
  neighbourhood: "osiedle",
  quarter: "osiedle",
  administrative: "jednostka adm.",
  municipality: "gmina",
  county: "powiat",
  state: "województwo",
  residential: "ulica",
  road: "ulica",
  street: "ulica",
  house: "budynek",
  building: "budynek",
  river: "rzeka",
  stream: "ciek",
  water: "woda",
  lake: "jezioro",
  forest: "las",
  wood: "las",
  substation: "stacja elektroen.",
  power: "obiekt energetyczny",
  railway: "kolej",
  peak: "szczyt",
  postcode: "kod pocztowy",
};

function placeKindLabel(p: MegamapPlace): string {
  if (p.kind === "address") return "adres";
  if (p.kind === "parcel") return "działka";
  const t = (p.place_type || "").trim();
  return PLACE_TYPE_PL[t] || t || "miejsce";
}

/** What went wrong with the place lookup, in words the user can act on. 429 is ours
 * (the OSM rate-limit guard), anything else is the external service or the network. */
function placeSearchErrorMessage(err: unknown): string {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 429) return "Za dużo wyszukiwań pod rząd — odczekaj chwilę.";
  return "Wyszukiwanie miejsc chwilowo niedostępne. Projekty po nazwie szukają się normalnie.";
}

/** Blue pin for a searched place — deliberately NOT the red of project dots, so a search
 * result is never mistaken for a project on the map. */
function placePinIcon(): L.DivIcon {
  return L.divIcon({
    html: `<div style="width:18px;height:18px;border-radius:9999px;border:3px solid #2563eb;background:#fff;box-shadow:0 1px 5px rgba(0,0,0,.35)"></div>`,
    className: "",
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}

function popupHtml(p: MegamapProject): string {
  const money = p.total_value != null ? `${formatAmount(p.total_value)} zł` : "—";
  const needsBadge = p.needs_subcontractor
    ? `<span style="background:#fee2e2;color:#b91c1c;border-radius:4px;padding:1px 5px;font-size:11px">do przydziału podwykonawcy</span>`
    : "";
  const coord = p.coordinator_names.length ? p.coordinator_names.join(", ") : "—";
  return `
    <div style="min-width:210px;font-size:12px;line-height:1.5">
      <div style="font-weight:600;margin-bottom:2px">${escapeHtml(p.name)}</div>
      <div style="color:#64748b">${escapeHtml(p.client_name ?? "—")}</div>
      ${needsBadge}
      <table style="margin-top:6px;border-collapse:collapse">
        <tr><td style="color:#64748b;padding-right:8px">Koordynator</td><td>${escapeHtml(coord)}</td></tr>
        <tr><td style="color:#64748b;padding-right:8px">Trasa</td><td>${fmtMeters(p.route_length_m)}</td></tr>
        <tr><td style="color:#64748b;padding-right:8px">Działki</td><td>${p.parcel_count} (${p.private_unagreed_parcels} nieuzg.)</td></tr>
        <tr><td style="color:#64748b;padding-right:8px">Wartość</td><td>${money}</td></tr>
        <tr><td style="color:#64748b;padding-right:8px">WPW</td><td>${p.has_wpw_order ? escapeHtml(p.wpw_contractor_names.join(", ") || "tak") : "brak"}</td></tr>
      </table>
      <button data-open-project="${p.project_id}" style="margin-top:8px;width:100%;background:#dc2626;color:#fff;border:none;border-radius:6px;padding:5px 8px;font-size:12px;cursor:pointer">Otwórz projekt</button>
    </div>`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string,
  );
}

export function MegamapCanvas({ rows, colorDim, sizeDim, lasso, focus, onViewportChange, onLassoSelect, onZoomChange, voivodeshipLines }: Props) {
  const navigate = useNavigate();
  const navRef = useRef(navigate);
  navRef.current = navigate;
  const onZoomChangeRef = useRef(onZoomChange);
  onZoomChangeRef.current = onZoomChange;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);
  const lassoLayerRef = useRef<L.Layer | null>(null);
  const rowsRef = useRef<MegamapProject[]>(rows);
  const colorRef = useRef<ColorDim>(colorDim);
  const sizeRef = useRef<SizeDim>(sizeDim);
  const [zoom, setZoom] = useState(6);

  // ── on-map search: project names (local) + places (geocoders) ──
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  // Picking a hit fills the box with its label. That text must NOT be sent back to the
  // geocoder as if the user had typed it — one pick would cost an extra external call
  // and answer with places that merely share the project's name.
  const [pickedLabel, setPickedLabel] = useState<string | null>(null);

  // Searches the rows currently on the map (post-filter). Located projects can be flown to;
  // unlocated ones (no route/parcel geometry) still match by name so they're findable — sorted last.
  const projectHits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [] as MegamapProject[];
    return rows
      .filter((p) => p.name.toLowerCase().includes(q))
      .sort((a, b) => Number(b.located) - Number(a.located))
      .slice(0, 8);
  }, [rows, query]);

  // Debounced: every distinct value here is one call to an externally rate-limited geocoder.
  const debouncedQuery = useDebouncedValue(query, 400);
  const {
    data: placeHits = [],
    isFetching: placesLoading,
    error: placesError,
  } = usePlaceSearch(debouncedQuery, searchOpen && debouncedQuery !== pickedLabel);
  // Awaria geokodera NIE MOŻE wyglądać jak „nic nie znaleziono" — człowiek zacząłby
  // szukać błędu u siebie i wpisywać hasło po hasłu, każde w martwą usługę.
  const placesErrorMsg = placesError ? placeSearchErrorMessage(placesError) : null;

  // One flat list so ↑/↓ walks projects and places as a single ladder.
  type Hit = { t: "project"; p: MegamapProject } | { t: "place"; p: MegamapPlace };
  const hits = useMemo<Hit[]>(
    () => [
      ...projectHits.map((p): Hit => ({ t: "project", p })),
      ...placeHits.map((p): Hit => ({ t: "place", p })),
    ],
    [projectHits, placeHits],
  );

  // The searched-place pin lives OUTSIDE markersRef — that group is cleared on every
  // redraw (pan/zoom/filter change) and the pin has to survive all of them.
  const placeMarkerRef = useRef<L.Marker | null>(null);
  const clearPlacePin = () => {
    const map = mapRef.current;
    if (placeMarkerRef.current && map) map.removeLayer(placeMarkerRef.current);
    placeMarkerRef.current = null;
  };

  const selectProject = (p: MegamapProject) => {
    const map = mapRef.current;
    setQuery(p.name);
    setPickedLabel(p.name);
    setSearchOpen(false);
    // No coordinates → nowhere to fly; open the project detail in a new tab (keeps the map open).
    if (!map || !p.located || p.lat == null || p.lng == null) {
      window.open(`/power-projects/${p.project_id}`, "_blank", "noopener,noreferrer");
      return;
    }
    map.flyTo([p.lat, p.lng], Math.max(map.getZoom(), 14));
    // Reuse the marker popup + the global popupopen handler wires its "Otwórz projekt" button.
    L.popup({ maxWidth: 300 }).setLatLng([p.lat, p.lng]).setContent(popupHtml(p)).openOn(map);
  };

  const selectPlace = (pl: MegamapPlace) => {
    const map = mapRef.current;
    setQuery(pl.label);
    setPickedLabel(pl.label);
    setSearchOpen(false);
    if (!map) return;
    clearPlacePin();
    if (pl.bbox) {
      // Frame the whole feature (a village, a commune) instead of flying to its label
      // point; the zoom cap keeps a one-building extent from slamming us to street level.
      const [south, north, west, east] = pl.bbox;
      map.flyToBounds(L.latLngBounds([south, west], [north, east]), { maxZoom: 15, padding: [40, 40] });
    } else {
      map.flyTo([pl.lat, pl.lng], Math.max(map.getZoom(), 14));
    }
    const marker = L.marker([pl.lat, pl.lng], { icon: placePinIcon(), zIndexOffset: 500 })
      .bindTooltip(`${pl.label}${pl.detail ? ` · ${pl.detail}` : ""}`, { direction: "top", offset: [0, -12] })
      .addTo(map);
    marker.openTooltip();
    placeMarkerRef.current = marker;
  };

  const selectHit = (h: Hit | undefined) => {
    if (!h) return;
    if (h.t === "project") selectProject(h.p);
    else selectPlace(h.p);
  };

  const gugikParcelsRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikInfraRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikOrthoRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikMpzpRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikGdosRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikKonserwatorRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikKonserwatorEwidencjaRef = useRef<L.TileLayer.WMS | null>(null);
  const gugikClosedAreasRef = useRef<ClosedAreasLayer | null>(null);
  const [gugik, setGugik] = useGugikState("powerProjects.megamap", {
    parcels: false, infra: false, ortho: false, mpzp: false, gdos: false, konserwator: false,
    konserwatorEwidencja: false, closedAreas: false,
  });

  // ── redraw clusters/markers for the current viewport ──
  const redraw = () => {
    const map = mapRef.current;
    const group = markersRef.current;
    if (!map || !group) return;
    group.clearLayers();
    const bounds = map.getBounds();
    const located = rowsRef.current.filter((p) => p.located);
    const visible = located.filter((p) => bounds.contains([p.lat as number, p.lng as number]));
    onViewportChange(visible);

    // Dot radius scales LOGARITHMICALLY with the chosen metric (default: tender value), so a
    // handful of huge projects don't dwarf everything else. log1p keeps 0 at the floor.
    const sizeD = sizeRef.current;
    const maxMetric = located.reduce((mx, p) => Math.max(mx, sizeMetric(sizeD, p)), 0);
    const logMax = Math.log1p(maxMetric);
    const radiusFor = (p: MegamapProject) => {
      if (sizeD === "uniform" || logMax <= 0) return 8;
      return 5 + 17 * (Math.log1p(sizeMetric(sizeD, p)) / logMax);
    };

    // Wider cells so neighbouring bubbles merge aggressively instead of overlapping.
    for (const c of pixelClusters(map, located, 230)) {
      if (c.items.length === 1) {
        const p = c.items[0];
        const r = radiusFor(p);
        L.circleMarker([p.lat as number, p.lng as number], {
          radius: r,
          color: "#fff",
          weight: 1.5,
          fillColor: colorFor(colorRef.current, p),
          fillOpacity: 0.95,
        })
          .bindPopup(popupHtml(p), { maxWidth: 300 })
          .addTo(group);
        // The "prostokąt" above the dot, same as clusters get (non-interactive so the dot stays clickable).
        L.marker([p.lat as number, p.lng as number], {
          icon: singleLabelIcon(p, r),
          interactive: false,
          keyboard: false,
        }).addTo(group);
      } else {
        const agg = aggregate(c.items);
        const marker = L.marker([c.lat, c.lng], {
          icon: clusterIcon(c.items.length, agg.private_unagreed_parcels, agg.private_owners_pending, agg.total_value, agg.needs_subcontractor),
        }).bindTooltip(
          `<b>${agg.count} projektów</b> · ${fmtMeters(agg.route_length_m)} tras · ${agg.parcel_count} działek łącznie<br><b>${agg.private_unagreed_parcels}</b> działek prywatnych nieuzgodnionych<br><b>${agg.private_owners_pending}</b> WPW nieuzgodnionych i niewysłanych w teren<br>Wartość: <b>${formatAmount(agg.total_value)} zł</b><br>${agg.needs_subcontractor} projektów do przydziału podwykonawcy`,
          { direction: "top", offset: [0, -8] },
        );
        marker.on("click", () => map.setView([c.lat, c.lng], Math.min(map.getZoom() + 2, 18)));
        marker.addTo(group);
      }
    }
  };

  // ── init map (once) ──
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, { center: [52.0, 19.0], zoom: 6, attributionControl: false });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 21, zIndex: 0 }).addTo(map);
    const g = createGugikLayers(map);
    gugikParcelsRef.current = g.parcels;
    gugikInfraRef.current = g.infra;
    gugikOrthoRef.current = g.ortho;
    gugikMpzpRef.current = g.mpzp;
    gugikGdosRef.current = g.gdos;
    gugikKonserwatorRef.current = g.konserwator;
    gugikKonserwatorEwidencjaRef.current = g.konserwatorEwidencja;
    gugikClosedAreasRef.current = g.closedAreas;
    const bordersPane = map.createPane(VOIVODESHIP_PANE);
    bordersPane.style.zIndex = "390";
    bordersPane.style.pointerEvents = "none";
    markersRef.current = L.layerGroup().addTo(map);
    const emitZoom = () => {
      const z = map.getZoom();
      setZoom(z);
      onZoomChangeRef.current(z);
    };
    map.on("moveend zoomend", redraw);
    map.on("zoomend", emitZoom);
    emitZoom();
    map.pm.setLang("pl");
    // "Otwórz projekt" inside a popup → SPA navigation (no full reload).
    map.on("popupopen", (e: L.PopupEvent) => {
      const btn = (e.popup.getElement() as HTMLElement | null)?.querySelector<HTMLButtonElement>("[data-open-project]");
      btn?.addEventListener("click", () => navRef.current(`/power-projects/${btn.dataset.openProject}`));
    });
    mapRef.current = map;
    // The map mounts inside a flex column — measure once layout has settled so tiles fill it.
    setTimeout(() => map.invalidateSize(), 0);
    redraw();
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useGugikToggle(mapRef, gugikParcelsRef, gugik.parcels);
  useGugikToggle(mapRef, gugikInfraRef, gugik.infra);
  useGugikToggle(mapRef, gugikOrthoRef, gugik.ortho);
  useGugikToggle(mapRef, gugikMpzpRef, gugik.mpzp);
  useGugikToggle(mapRef, gugikGdosRef, gugik.gdos);
  useGugikToggle(mapRef, gugikKonserwatorRef, !!gugik.konserwator);
  useGugikToggle(mapRef, gugikKonserwatorEwidencjaRef, !!gugik.konserwatorEwidencja);
  useGugikToggle(mapRef, gugikClosedAreasRef, !!gugik.closedAreas);
  const rejestrEmpty = useNidTileContent(gugikKonserwatorRef, !!gugik.konserwator);
  const ewidencjaEmpty = useNidTileContent(gugikKonserwatorEwidencjaRef, !!gugik.konserwatorEwidencja);
  const gugikLoading = useGugikLoading(
    gugikParcelsRef, gugikInfraRef, gugikOrthoRef, gugik, gugikMpzpRef, gugikGdosRef, gugikKonserwatorRef,
    gugikKonserwatorEwidencjaRef, undefined, gugikClosedAreasRef,
  );

  // Redraw whenever the data, colour or size dimension changes.
  useEffect(() => {
    rowsRef.current = rows;
    colorRef.current = colorDim;
    sizeRef.current = sizeDim;
    redraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, colorDim, sizeDim]);

  // ── voivodeship borders ──
  const { data: counties } = useCountyBoundaries();
  const borders = useMemo(() => voivodeshipBorders(counties ?? []), [counties]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !voivodeshipLines || borders.length === 0) return;
    const latlngs = borders.map((line) => line.map(([lng, lat]) => [lat, lng] as L.LatLngTuple));
    // A white casing under a dark line keeps the border readable on OSM and the ortofotomapa alike.
    const layer = L.layerGroup([
      L.polyline(latlngs, { pane: VOIVODESHIP_PANE, color: "#ffffff", weight: 6, opacity: 0.85, interactive: false }),
      L.polyline(latlngs, { pane: VOIVODESHIP_PANE, color: "#1e293b", weight: 2.5, opacity: 0.95, interactive: false }),
    ]).addTo(map);
    return () => {
      layer.remove();
    };
  }, [borders, voivodeshipLines]);

  // Fly to a focus point (zone click).
  useEffect(() => {
    if (focus && mapRef.current) mapRef.current.flyTo([focus.lat, focus.lng], 11);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.key]);

  // ── lasso (geoman polygon draw) ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const clearLasso = () => {
      if (lassoLayerRef.current) {
        map.removeLayer(lassoLayerRef.current);
        lassoLayerRef.current = null;
      }
    };
    if (!lasso) {
      map.pm.disableDraw();
      clearLasso();
      return;
    }
    map.pm.enableDraw("Polygon", { snappable: false, templineStyle: { color: "#dc2626" }, pathOptions: { color: "#dc2626", fillOpacity: 0.08 } });
    const onCreate = (e: { layer: L.Layer }) => {
      clearLasso();
      lassoLayerRef.current = e.layer;
      const latlngs = (e.layer as L.Polygon).getLatLngs()[0] as L.LatLng[];
      const ring = latlngs.map((ll) => [ll.lng, ll.lat] as [number, number]);
      const picked = rowsRef.current.filter(
        (p) => p.located && pointInPolygon(p.lng as number, p.lat as number, ring),
      );
      onLassoSelect(picked);
      map.pm.disableDraw();
    };
    map.on("pm:create", onCreate);
    return () => {
      map.off("pm:create", onCreate);
      map.pm.disableDraw();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lasso]);

  return (
    // `isolate` contains the Leaflet/GUGiK z-indexes (panes up to 650, controls and our
    // panels at 1000) inside the map box, so the notifications dropdown and modals paint
    // above the map — same as MapTab / ZEDrawingMap.
    <div className="relative isolate z-0 h-full w-full">
      <div ref={containerRef} className="h-full w-full" />
      {/* `top-[78px]` = POD leafletowym +/− (kontrolka zoomu kończy się na ~70 px);
          panel z z-index 1000 w lewym górnym rogu zasłaniałby przyciski zoomu. Ta sama
          wysokość co w MapTab i ZEDrawingMap. */}
      <GugikLayerPanel
        gugik={gugik}
        onChange={setGugik}
        zoom={zoom}
        loading={gugikLoading}
        className="absolute top-[78px] left-2"
        nidEmpty={{ rejestr: rejestrEmpty, ewidencja: ewidencjaEmpty }}
      />

      {/* On-map search: project name OR a place (address, locality, parcel TERYT) → map flies to it. */}
      <div className="absolute left-1/2 top-2 z-[1000] w-80 -translate-x-1/2">
        <div className="relative">
          <Search size={15} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPickedLabel(null); setSearchOpen(true); setActiveIdx(0); }}
            onFocus={() => setSearchOpen(true)}
            onBlur={() => setTimeout(() => setSearchOpen(false), 120)}
            onKeyDown={(e) => {
              if (e.key === "Escape") { setSearchOpen(false); return; }
              if (!searchOpen || hits.length === 0) return;
              if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, hits.length - 1)); }
              else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
              else if (e.key === "Enter") { e.preventDefault(); selectHit(hits[activeIdx] ?? hits[0]); }
            }}
            placeholder="Szukaj projektu, miejscowości lub adresu…"
            className="w-full rounded-md border border-gray-300 bg-white/95 py-2 pl-8 pr-8 text-sm shadow-md outline-none backdrop-blur focus:border-red-400 dark:border-gray-600 dark:bg-gray-900/95"
          />
          {placesLoading && (
            <Loader2 size={14} className="absolute right-8 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />
          )}
          {query && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { setQuery(""); setPickedLabel(null); setSearchOpen(false); clearPlacePin(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              title="Wyczyść (usuwa też pinezkę wyszukanego miejsca)"
            >
              <X size={14} />
            </button>
          )}
        </div>
        {searchOpen && hits.length > 0 && (
          <div className="mt-1 max-h-80 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
            {projectHits.length > 0 && (
              <div className="border-b border-gray-100 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:border-gray-800">
                Projekty
              </div>
            )}
            {projectHits.map((p, i) => (
              <button
                key={p.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectProject(p)}
                onMouseEnter={() => setActiveIdx(i)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left ${
                  i === activeIdx ? "bg-red-50 dark:bg-gray-800" : "hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <MapPin size={13} className={`shrink-0 ${p.located ? "text-red-500" : "text-gray-300 dark:text-gray-600"}`} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm">{p.name}</span>
                  <span className="block truncate text-xs text-gray-400">
                    {p.client_name ?? "—"}
                    {!p.located && <span className="text-amber-600 dark:text-amber-500"> · bez lokalizacji</span>}
                  </span>
                </span>
              </button>
            ))}
            {placesErrorMsg && (
              <div className="border-t border-gray-100 px-3 py-2 text-xs text-amber-700 dark:border-gray-800 dark:text-amber-500">
                {placesErrorMsg}
              </div>
            )}
            {placeHits.length > 0 && (
              <div className="border-b border-t border-gray-100 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:border-gray-800">
                Miejsca
              </div>
            )}
            {placeHits.map((pl, i) => {
              const idx = projectHits.length + i;
              return (
                <button
                  key={`${pl.kind}-${pl.lat}-${pl.lng}-${pl.label}`}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectPlace(pl)}
                  onMouseEnter={() => setActiveIdx(idx)}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left ${
                    idx === activeIdx ? "bg-blue-50 dark:bg-gray-800" : "hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                >
                  <Navigation size={13} className="shrink-0 text-blue-600" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">{pl.label}</span>
                    <span className="block truncate text-xs text-gray-400">
                      {placeKindLabel(pl)}
                      {pl.detail ? ` · ${pl.detail}` : ""}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
        {searchOpen && query.trim() && hits.length === 0 && (
          <div
            className={`mt-1 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg dark:border-gray-700 dark:bg-gray-900 ${
              placesErrorMsg && !placesLoading
                ? "text-amber-700 dark:text-amber-500"
                : "text-gray-500"
            }`}
          >
            {placesLoading
              ? "Szukam…"
              : placesErrorMsg
                ? placesErrorMsg
                : query.trim().length < PLACE_SEARCH_MIN
                  ? "Wpisz co najmniej 3 znaki, żeby szukać miejscowości i adresów."
                  : "Nic nie znaleziono — ani projektu, ani miejsca o tej nazwie."}
          </div>
        )}
      </div>
    </div>
  );
}
